import { useRef, useState, useEffect, useCallback } from 'react';

const DEFAULT_THRESHOLD_MS = 30000; // 30 seconds

const useSearchNotification = (isLoading, { thresholdMs = DEFAULT_THRESHOLD_MS } = {}) => {
  const [thresholdExceeded, setThresholdExceeded] = useState(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const thresholdExceededRef = useRef(false);

  // Start timer when isLoading becomes true
  useEffect(() => {
    if (isLoading) {
      startTimeRef.current = Date.now();
      setThresholdExceeded(false);
      thresholdExceededRef.current = false;

      timerRef.current = setTimeout(() => {
        setThresholdExceeded(true);
        thresholdExceededRef.current = true;
      }, thresholdMs);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isLoading, thresholdMs]);

  const fireNotification = useCallback(() => {
    const notification = new Notification('Your map is ready!', {
      body: 'Your relationship graph has finished loading.',
      icon: '/manifest.json',
      tag: 'search-complete',
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  }, []);

  const sendNotification = useCallback(() => {
    if (!('Notification' in window)) return;

    if (Notification.permission === 'granted') {
      fireNotification();
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          fireNotification();
        }
      });
    }
  }, [fireNotification]);

  // Fire notification when isLoading transitions false AND threshold was exceeded
  const prevLoadingRef = useRef(isLoading);
  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = isLoading;

    if (wasLoading && !isLoading && thresholdExceededRef.current) {
      sendNotification();
      thresholdExceededRef.current = false;
      setThresholdExceeded(false);
    }
  }, [isLoading, sendNotification]);

  return { thresholdExceeded };
};

export default useSearchNotification;
